<template>
  <div>
    <v-form ref="form">
      <v-toolbar dense color="white">
        <v-toolbar-title>防災資機材の配備状況取込</v-toolbar-title>
      </v-toolbar>
      <v-container fluid>
        <application-error-message :error-object="applicationErrorObject" />
        <error-message :error-object="errorObject" />
        <success-message :success-object="successObject" />
        <p style="margin: 5px 0 15px 0">
          原子力防災資機材管理システム(略称:NEMS)からダウンロードした「防災資機材の配備状況」のEXCELファイルを確認の上、あらかじめ、以下の手順で用意されたCSV形式のファイルをアップロードすることで、現在登録されている情報を削除した後に再登録します。<br />なお、エラーが発生した場合は、NEMSにて確認の上、正しいファイルにて同様の操作を行い、再度アップロード操作を行ってください。
        </p>
        <p style="margin: 5px 0 15px 0">
          【CSVファイルの保存手順概要】<br />EXCELの「ファイル」メニューより、「名前を付けて保存」を選択後、ファイルの種類に「CSV(コンマ区切り)」を選択し「保存」することで変換されます。
        </p>
        <v-file-input
          v-model="file"
          accept=".csv"
          placeholder="ファイルを選択してください"
          prepend-icon="mdi-paperclip"
          outlined
        >
          <template v-slot:selection="{ text }">
            <v-chip small label color="primary">
              {{ text }}
            </v-chip>
          </template>
        </v-file-input>
        <ErrorDialog ref="errorDialog" />
      </v-container>
      <div class="text-right">
        <v-btn
          color="primary"
          @click="confirm"
          :disabled="!canUpload || validationFlag"
        >
          アップロード
        </v-btn>
      </div>
    </v-form>
    <v-dialog v-model="confirmDialog" persistent max-width="400">
      <v-card>
        <v-card-title class="headline">アップロード確認</v-card-title>
        <v-card-text>
          現在登録済みのデータを削除し再登録します。<br />
          アップロードしてもよろしいですか？
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="submit" :disabled="processingFlag">
            アップロード
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmDialog = false"
            :disabled="processingFlag"
          >
            キャンセル
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-overlay :value="overlay" opacity="1" color="white" z-index="2147483647">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import ErrorDialog from "@/components/ErrorDialog";
import { ScreenId } from "@/js/value-objects/screen-id";
import { RepositoryFactory } from "@/js/repository/RepositoryFactory";

import handleApplicationError from "@/mixins/handle-application-error";
import messageUtils from "@/mixins/message-utils";

const equipmentDeployRepository = RepositoryFactory.get("equipmentDeploy");

export default {
  mixins: [handleApplicationError, messageUtils],
  components: {
    ErrorDialog,
    ApplicationErrorMessage: () =>
      import("@/components/ApplicationErrorMessage"),
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage")
  },
  data: () => ({
    overlay: false,
    file: null,
    validationFlag: false,
    processingFlag: false,
    confirmDialog: false
  }),

  computed: {
    /**
     * アップロード可能かどうかを判定する。
     * @returns {bool} 防災資機材の配備状況ファイルが選択されている場合 true、その他の場合 false。
     */
    canUpload() {
      return !!this.file;
    }
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    overlay(val) {
      val &&
        setTimeout(() => {
          this.overlay = false;
        }, 3000);
    },
    $route: "initialize"
  },

  created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      this.overlay = true;
      const screen = this.$screenOperationAuthority(
        ScreenId.SF1902,
        ScreenId.SF1902
      );
      this.readOnly = screen.readOnly;
      this.screenId = screen.screenId;
      this.screenTitle = screen.screenTitle;
      this.handleSuccess(this.$route.params);

      this.overlay = false;
    },

    confirm() {
      if (this.file?.name.toLowerCase().endsWith(".csv")) {
        this.confirmDialog = true;
      } else {
        this.showErrorDialog(
          "ファイル形式が正しくありません。",
          "CSVファイルを選択してください。"
        );
      }
    },

    async submit() {
      this.clearMessage();
      this.clearApplicationErrorMessage();
      this.overlay = true;
      this.processingFlag = true;
      try {
        // 防災資機材の配備状況登録API呼出
        const params = new FormData();
        params.append("file", this.file);
        await equipmentDeployRepository
          .entry(this.screenId, params)
          .then(response => {
            this.handleSuccess(response);
          });
      } catch (error) {
        if (error.response != null) {
          if (error.response.data.error.code === "ERAE47011") {
            this.handleCreateApplicationError(error);
          } else {
            this.handleCreateError(error);
          }
        } else {
          this.handleCreateError(error);
        }
        window.scrollTo(0, 0);
      }
      this.confirmDialog = false;
      this.processingFlag = false;
      this.overlay = false;
    },

    /**
     * エラーダイアログを表示する。
     * @param {...string} messages メッセージ
     */
    showErrorDialog(...messages) {
      this.$refs.errorDialog.open(messages);
    }
  }
};
</script>
